/**
 * Inbound threats: every tracked hostile missile (not ours, not an ally's) that will land on
 * own or allied ground, with what the player needs to react — a place name, the ETA, what
 * the blast puts at risk and whether a friendly SAM covers the impact point. Pure; the store
 * recomputes the list once per frame (tested in test/threats.test.ts).
 */
import { isFriendlyTo } from '@sa/protocol';
import {
  BALANCE,
  TICK_HZ,
  axialDist,
  axialOfPoint,
  getGeo,
  missilePos,
  samRange,
  type EntityId,
  type EntityKind,
  type HexIdx,
  type MissileKind,
  type PlayerId,
  type PlayerView,
  type Tick,
  type WorldMap,
} from '@sa/sim';
import { compass, kmBetween, nearestLandHex } from './geo';

const mmss = (secs: number) => {
  const s = Math.max(0, Math.ceil(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export interface InboundThreat {
  /** missile track id */
  id: EntityId;
  kind: MissileKind;
  owner: PlayerId;
  /** impact point (mu) */
  target: { x: number; y: number };
  /** missile head now (mu), on the shared ballistic path */
  head: { x: number; y: number };
  /**
   * Where it lands, for people: the named city at the impact point (within ~1.5 hexes), else
   * a bearing from the nearest named city, e.g. "120 KM N OF PARIS". Never "TERRITORY".
   */
  label: string;
  /**
   * What the blast puts at risk (rings 0–1): "CAPITAL · CRITICAL", "CAPITAL", "CITY L3", "SILO",
   * "AIRBASE", "SAM", "RADAR" (prefixed "ALLY " on allied land), or null for open ground.
   */
  stake: string | null;
  /** it lands on a critical capital (§1.11): one more hit there eliminates — always the top threat */
  critical: boolean;
  impactTick: number;
  /** seconds until impact at the frame's tick, ≥ 0 (fractional) */
  etaSeconds: number;
  /** an own / allied SAM site (or own carrier) with ammo has the impact point in range */
  samCovered: boolean;
  /** those SAMs / carriers (for a "these can engage" pulse) */
  coveringSams: EntityId[];
  /** our own finished city the blast would hit (rings 0–1): the one a brace protects, or null */
  cityId: EntityId | null;
  /** that city is braced through the impact: seconds of brace left (null: not braced, or it runs out first) */
  bracedSeconds: number | null;
  /** the impact point is inside the viewport */
  onScreen: boolean;
  /** the missile head is inside the viewport (else: edge chevron) */
  headOnScreen: boolean;
  /**
   * Only an ally's ground and things are at stake (nothing of ours): shown calmly, never as
   * our alarm, and outranked by everything about our own nation.
   */
  ally: boolean;
  /** who owns the land it falls on (as far as we know) */
  landOwner: PlayerId;
}

/**
 * An ally's threat worth our attention: one that puts the ally's city, capital or silo at stake.
 * Their open ground (or a radar) is theirs to watch: no alert line, no Space stop.
 */
export const allyStakeMatters = (t: InboundThreat): boolean => t.ally && !!t.stake && /^ALLY (CITY|CAPITAL|SILO)/.test(t.stake);

/** Visible world rect [x0, y0, x1, y1] (mu), e.g. `Camera.visibleRect()`. */
export type ViewRect = readonly [number, number, number, number];

/** A named city closer than this many hex steps to the impact names the target itself. */
const NAME_HEXES = 1.5;
const SQRT3 = Math.sqrt(3);

const STAKE_RANK: Partial<Record<EntityKind, number>> = { silo: 3, airbase: 4, sam: 5, radar: 6 };

/** Static parts of a threat (target never moves): cached per track id by `ThreatTracker`. */
interface ThreatPlace {
  label: string;
}

export interface ThreatContext {
  world: WorldMap;
  /** match-start city names by hex (`cityNames`) */
  names: ReadonlyMap<HexIdx, string>;
  /** every named place on the map (map cities + `names`), for "120 KM N OF …" bearings */
  refs?: ReadonlyMap<HexIdx, string>;
  /** capital hexes of us and our allies */
  capitals: ReadonlySet<HexIdx>;
  /**
   * when each critical capital went critical (tick): hits in the grace after that cost nothing
   * (§1.11), so a warhead landing inside it is no critical threat. Missing: no grace known.
   */
  criticalSince?: ReadonlyMap<PlayerId, Tick>;
}

/** The suffix a critical capital's stake carries. */
export const CRITICAL_STAKE = 'CAPITAL · CRITICAL';

/**
 * Would a hit on `owner`'s capital landing at `impactTick` be lethal-critical (§1.11)? Its
 * capital is critical then (the window runs past the impact) and the hit lands after the grace.
 */
export function criticalAt(view: PlayerView, owner: PlayerId, impactTick: number, since?: Tick): boolean {
  const until = view.criticalCapitals.get(owner);
  if (until === undefined || impactTick > until) return false;
  return since === undefined || impactTick >= since + BALANCE.capital.criticalGraceTicks;
}

function nearestNamed(world: WorldMap, names: ReadonlyMap<HexIdx, string>, x: number, y: number): { hex: HexIdx; d: number } {
  let hex: HexIdx = -1;
  let d2 = Infinity;
  for (const h of names.keys()) {
    const d = (world.cx[h] - x) ** 2 + (world.cy[h] - y) ** 2;
    if (d < d2) {
      d2 = d;
      hex = h;
    }
  }
  return { hex, d: Math.sqrt(d2) };
}

/**
 * "PARIS" if a match city sits at (x, y), "NEAR TIMBUKTU" at another named place, else
 * "120 KM N OF PARIS" from the nearest named place (`refs`, default: the match cities).
 */
export function placeLabel(world: WorldMap, names: ReadonlyMap<HexIdx, string>, x: number, y: number, refs: ReadonlyMap<HexIdx, string> = names): string {
  const at = nearestNamed(world, names, x, y);
  if (at.hex >= 0 && at.d <= NAME_HEXES * SQRT3 * world.hexR) return names.get(at.hex)!;
  const ref = nearestNamed(world, refs, x, y);
  if (ref.hex < 0) return 'OPEN GROUND';
  if (ref.d <= NAME_HEXES * SQRT3 * world.hexR) return `NEAR ${refs.get(ref.hex)!}`;
  const cx = world.cx[ref.hex];
  const cy = world.cy[ref.hex];
  const km = Math.max(10, Math.round(kmBetween(cx, cy, x, y) / 10) * 10);
  return `${km} KM ${compass(cx, cy, x, y)} OF ${refs.get(ref.hex)!}`;
}

/**
 * What a blast at (x, y) would cost us or an ally (rings 0–1), or null. `ownOnly`: only our own
 * things. A critical capital (`critical(owner)`, default: critical now) reads "CAPITAL · CRITICAL".
 */
export function blastStake(
  view: PlayerView,
  world: WorldMap,
  capitals: ReadonlySet<HexIdx>,
  x: number,
  y: number,
  ownOnly = false,
  critical: (owner: PlayerId) => boolean = (p) => view.criticalCapitals.has(p),
): string | null {
  const g = getGeo(world);
  const [q, r] = axialOfPoint(g, x, y);
  let best: { rank: number; text: string; d: number } | null = null;
  for (const e of view.entities.values()) {
    if (!(ownOnly ? e.owner === view.me : isFriendlyTo(view, e.owner)) || e.state === 'building') continue;
    const [eq, er] = axialOfPoint(g, e.x, e.y);
    const d = axialDist(q, r, eq, er);
    let rank: number;
    let text: string;
    if (e.kind === 'city') {
      if (d > 1) continue;
      const hex = nearestLandHex(world, e.x, e.y);
      const level = e.detail?.level ?? e.extra;
      const cap = capitals.has(hex);
      const crit = cap && critical(e.owner);
      rank = crit ? -1 : cap ? 0 : 10 - level;
      text = crit ? CRITICAL_STAKE : cap ? 'CAPITAL' : `CITY L${level}`;
    } else {
      const k = STAKE_RANK[e.kind];
      // silos only die to ring 0, other structures to rings 0–1 (§1.8)
      if (k === undefined || d > (e.kind === 'silo' ? 0 : 1)) continue;
      rank = 10 + k;
      text = e.kind.toUpperCase();
    }
    if (!best || rank < best.rank || (rank === best.rank && d < best.d)) best = { rank, text: e.owner === view.me ? text : `ALLY ${text}`, d };
  }
  return best?.text ?? null;
}

/**
 * Our own finished city a blast at (x, y) would hit (rings 0–1): the capital first, then the
 * highest level, then the nearest. Null if none (a brace only helps a city).
 */
export function braceTarget(view: PlayerView, world: WorldMap, capitals: ReadonlySet<HexIdx>, x: number, y: number): EntityId | null {
  const g = getGeo(world);
  const [q, r] = axialOfPoint(g, x, y);
  let best: { id: EntityId; rank: number } | null = null;
  for (const e of view.entities.values()) {
    if (e.kind !== 'city' || e.owner !== view.me || e.state === 'building') continue;
    const [eq, er] = axialOfPoint(g, e.x, e.y);
    const d = axialDist(q, r, eq, er);
    if (d > 1) continue;
    const rank = (capitals.has(nearestLandHex(world, e.x, e.y)) ? 0 : 20 - (e.detail?.level ?? e.extra)) * 4 + d;
    if (!best || rank < best.rank) best = { id: e.id, rank };
  }
  return best?.id ?? null;
}

/**
 * Own / allied SAM sites with ammo (and own carriers with SAM ammo) that reach (x, y) — or, given
 * the rest of a warhead's flight (`path`: ground points still ahead of it), any point of it: a SAM
 * engages a warhead wherever it passes within range, not only over the impact (so a missile a
 * SAM on its way can shoot is never called NO COVER).
 */
export function coveringSams(view: PlayerView, x: number, y: number, path: readonly { x: number; y: number }[] = []): EntityId[] {
  const out: EntityId[] = [];
  for (const e of view.entities.values()) {
    if (!isFriendlyTo(view, e.owner) || e.state === 'building') continue;
    let range = 0;
    let ammo = 0;
    if (e.kind === 'sam') {
      // (a silent SAM of ours reaches half as far; an ally's mode is not ours to know)
      range = samRange(!!e.detail?.silent);
      ammo = e.detail?.ammo ?? e.extra;
    } else if (e.kind === 'carrier') {
      range = BALANCE.carrier.samRange;
      // an ally's carrier ammo is not visible to us: assume it can shoot
      ammo = e.owner === view.me ? (e.detail?.ammo ?? 0) : 1;
    } else continue;
    if (ammo <= 0) continue;
    const r2 = range * range;
    if ((e.x - x) ** 2 + (e.y - y) ** 2 <= r2 || path.some((p) => (e.x - p.x) ** 2 + (e.y - p.y) ** 2 <= r2)) out.push(e.id);
  }
  return out;
}

/** Is (x, y) on own or allied land, as far as the view knows? */
export function landsOnFriendly(view: PlayerView, world: WorldMap, x: number, y: number): boolean {
  const h = nearestLandHex(world, x, y);
  return h >= 0 && isFriendlyTo(view, view.owner[h]);
}

const inRect = (r: ViewRect | null, x: number, y: number) => !r || (x >= r[0] && x <= r[2] && y >= r[1] && y <= r[3]);

/** Capital hexes of us and our allies. */
export function friendlyCapitals(view: PlayerView, world: WorldMap): Set<HexIdx> {
  const out = new Set<HexIdx>();
  for (const p of view.players) {
    if (!isFriendlyTo(view, p.id)) continue;
    const reg = world.regions.find((r) => r.id === p.regionId);
    if (reg) out.add(reg.capitalHex);
  }
  return out;
}

/**
 * Threats grouped by target (a salvo on one city is one group), most urgent group first;
 * within a group the most urgent missile first. Two impacts this close (mu) share a target.
 */
export function threatGroups(threats: readonly InboundThreat[], sameMu: number): InboundThreat[][] {
  const groups: InboundThreat[][] = [];
  for (const t of threats) {
    const g = groups.find((g) => {
      const h = g[0];
      return (t.cityId !== null && t.cityId === h.cityId) || (t.target.x - h.target.x) ** 2 + (t.target.y - h.target.y) ** 2 <= sameMu * sameMu;
    });
    if (g) g.push(t);
    else groups.push([t]);
  }
  return groups;
}

/**
 * The caption of an off-screen target group's edge chevron: what of ours is at stake and when, in
 * two words — `SILO T-0:11`, `CITY L3 T-0:24`, `CAPITAL T-0:09 ×2`. Null when nothing of ours is at
 * stake there (open ground, an ally's): no chevron at all.
 */
export function edgeCaption(group: readonly InboundThreat[]): string | null {
  const t = group.find((x) => x.stake && !x.ally);
  if (!t) return null;
  const what = t.critical ? 'CAPITAL' : t.stake!.replace(/ · .*$/, '');
  return `${what} T-${mmss(group[0].etaSeconds)}${group.length > 1 ? ` ×${group.length}` : ''}`;
}

/** points along the rest of a warhead's flight checked for SAM cover */
const PATH_SAMPLES = 24;

/** Keeps per-track static data (labels) between frames. */
export class ThreatTracker {
  private places = new Map<EntityId, ThreatPlace>();

  /**
   * All inbound threats at `tick`, the most important first (`threatRank`: what is at stake, then
   * the soonest) — a capital never sits behind a silo. `rect` = visible world rect (null: all on screen).
   */
  compute(view: PlayerView, ctx: ThreatContext, tick: number, rect: ViewRect | null): InboundThreat[] {
    const out: InboundThreat[] = [];
    const head = { x: 0, y: 0 };
    for (const t of view.tracks.values()) {
      const impact = t.launchTick + t.flightTicks;
      if (impact < tick || isFriendlyTo(view, t.owner)) continue;
      if (!landsOnFriendly(view, ctx.world, t.tx, t.ty)) continue;
      let place = this.places.get(t.id);
      if (!place) {
        place = { label: placeLabel(ctx.world, ctx.names, t.tx, t.ty, ctx.refs) };
        this.places.set(t.id, place);
      }
      missilePos(t, Math.max(t.launchTick, tick), head);
      // (the rest of its flight: a SAM it passes on the way covers it too)
      const now = Math.max(t.launchTick, tick);
      const ahead: { x: number; y: number }[] = [];
      for (let k = 0; k <= PATH_SAMPLES; k++) {
        const p = { x: 0, y: 0 };
        missilePos(t, now + ((impact - now) * k) / PATH_SAMPLES, p);
        ahead.push(p);
      }
      const sams = coveringSams(view, t.tx, t.ty, ahead);
      const crit = (p: PlayerId) => criticalAt(view, p, impact, ctx.criticalSince?.get(p));
      const own = blastStake(view, ctx.world, ctx.capitals, t.tx, t.ty, true, crit);
      const landOwner = view.owner[nearestLandHex(ctx.world, t.tx, t.ty)];
      const ally = !own && landOwner !== view.me;
      const stake = ally ? blastStake(view, ctx.world, ctx.capitals, t.tx, t.ty, false, crit) : own;
      const cityId = braceTarget(view, ctx.world, ctx.capitals, t.tx, t.ty);
      const until = cityId !== null && view.entities.get(cityId)?.state === 'braced' ? view.entities.get(cityId)?.detail?.braceUntil : undefined;
      out.push({
        id: t.id,
        kind: t.kind,
        owner: t.owner,
        target: { x: t.tx, y: t.ty },
        head: { x: head.x, y: head.y },
        label: place.label,
        stake,
        critical: !!stake?.endsWith(CRITICAL_STAKE),
        impactTick: impact,
        etaSeconds: Math.max(0, (impact - tick) / TICK_HZ),
        samCovered: sams.length > 0,
        coveringSams: sams,
        cityId,
        bracedSeconds: until !== undefined && until >= impact ? Math.max(0, (until - tick) / TICK_HZ) : null,
        onScreen: inRect(rect, t.tx, t.ty),
        headOnScreen: inRect(rect, head.x, head.y),
        ally,
        landOwner,
      });
    }
    for (const id of this.places.keys()) if (!view.tracks.has(id)) this.places.delete(id);
    out.sort((a, b) => threatRank(a) - threatRank(b) || a.impactTick - b.impactTick || a.id - b.id);
    return out;
  }
}

/**
 * How much a threat puts at stake, most first: a critical capital (one more hit ends the match) 0,
 * the capital 1, a city 2, a silo / SAM / other structure 3, open ground (or a city braced
 * through the impact: nothing to lose) 4. Ties go to the soonest.
 */
export function threatRank(t: Pick<InboundThreat, 'stake' | 'critical' | 'bracedSeconds'>): number {
  if (t.critical) return 0;
  const s = t.stake?.replace(/^ALLY /, '') ?? '';
  if (!s || t.bracedSeconds !== null) return 4;
  if (s.startsWith('CAPITAL')) return 1;
  if (s.startsWith('CITY')) return 2;
  return 3;
}
